import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { fetchDashboard } from "@/lib/api";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/landing/Logo";
import { toast } from "sonner";
import {
  LogOut, ExternalLink, LayoutDashboard, FileText, EyeOff, Star,
  IndianRupee, MessageSquare, HelpCircle, Building2, Plug, Image as ImageIcon,
  Activity, ShieldCheck, Database, Globe, Sparkles,
  ArrowUpRight, Clock, Folder,
  Megaphone, MessageCircle, Layers, Settings2, ChevronRight, CheckCircle2,
  RefreshCcw, Bell, Search, Inbox, XCircle, AlertTriangle, Rocket, X, Users, Mail,
} from "lucide-react";

/* ---------- helpers ---------- */
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};
const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
};
const greeting = () => {
  const h = new Date().getHours();
  if (h < 5) return "Working late";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Good night";
};
const initials = (s = "") => s.split("@")[0].slice(0, 2).toUpperCase();

/* ---------- atoms ---------- */
const StatTile = ({ icon: Icon, label, value, hint, tone = "blue", onClick, testId }) => {
  const tones = {
    blue:    "from-blue-50 to-white text-blue-700 ring-blue-100",
    emerald: "from-emerald-50 to-white text-emerald-700 ring-emerald-100",
    amber:   "from-amber-50 to-white text-amber-700 ring-amber-100",
    violet:  "from-violet-50 to-white text-violet-700 ring-violet-100",
    rose:    "from-rose-50 to-white text-rose-700 ring-rose-100",
    slate:   "from-slate-50 to-white text-slate-700 ring-slate-200",
    teal:    "from-teal-50 to-white text-teal-700 ring-teal-100",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="group relative text-left rounded-2xl bg-white border border-slate-200/80 p-4 sm:p-5 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgb(2,6,23,0.06)] transition-all duration-300 overflow-hidden"
    >
      <div className={`pointer-events-none absolute -top-10 -right-10 h-28 w-28 rounded-full bg-gradient-to-br ${tones[tone]} opacity-70`} />
      <div className="relative flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.12em] font-bold text-slate-500">{label}</span>
        <span className={`h-8 w-8 rounded-xl ring-1 flex items-center justify-center bg-gradient-to-br ${tones[tone]}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="relative mt-3 flex items-baseline gap-2">
        <span className="text-3xl font-extrabold text-slate-900 tabular-nums" style={{ fontFamily: "Outfit, sans-serif" }}>
          {value ?? "—"}
        </span>
        {hint && <span className="text-xs text-slate-500">{hint}</span>}
      </div>
    </button>
  );
};

const StatusPill = ({ ok, label, sub, icon: Icon }) => (
  <div className="flex items-center gap-3 rounded-xl border border-slate-200/80 bg-white p-3">
    <div className={`h-9 w-9 shrink-0 rounded-lg flex items-center justify-center ${ok ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
      <Icon className="h-4 w-4" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-semibold text-slate-900">{label}</span>
        <span className={`h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-500 animate-pulse" : "bg-rose-500"}`} />
      </div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 truncate">{sub}</div>
    </div>
  </div>
);

const SeoRow = ({ label, ok }) => (
  <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-700">{label}</span>
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full ${ok ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-amber-50 text-amber-700 border border-amber-100"}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-amber-500"}`} />
      {ok ? "Configured" : "Missing"}
    </span>
  </div>
);

const SECTION_LABELS = {
  hero: "Hero", features: "Features", pricing: "Pricing", testimonials: "Testimonials",
  faq: "FAQ", industries: "Industries", integrations: "Integrations",
  trust_stats: "Trust & client logos", crm: "CRM section", chatbot: "Chatbot section",
  bulk_messaging: "Bulk messaging", final_cta: "Final CTA",
  seo: "SEO settings", global_settings: "Global settings", footer: "Footer",
  navbar: "Navigation",
};
const ACTION_META = {
  "cms.update":   { verb: "updated",   tone: "bg-blue-50 text-blue-700",       icon: FileText },
  "cms.create":   { verb: "created",   tone: "bg-emerald-50 text-emerald-700", icon: Layers },
  "cms.delete":   { verb: "deleted",   tone: "bg-rose-50 text-rose-700",       icon: EyeOff },
  "cms.upload":   { verb: "uploaded",  tone: "bg-violet-50 text-violet-700",   icon: ImageIcon },
  "cms.reorder":  { verb: "reordered", tone: "bg-amber-50 text-amber-700",     icon: Layers },
  "media.delete": { verb: "deleted",   tone: "bg-rose-50 text-rose-700",       icon: ImageIcon },
  "auth.login":   { verb: "signed in", tone: "bg-slate-100 text-slate-700",    icon: ShieldCheck },
  "auth.logout":  { verb: "signed out",tone: "bg-slate-100 text-slate-700",    icon: LogOut },
};
const friendlyActivity = (item) => {
  const meta = ACTION_META[item.action];
  if (!meta) return { text: item.action, tone: "bg-slate-100 text-slate-700", icon: Activity };
  let text = meta.verb;
  const t = item.target;
  if (item.action === "cms.upload") {
    text = `Image uploaded${t ? ` · ${t}` : ""}`;
  } else if (item.action === "media.delete") {
    text = `Media deleted${t ? ` · ${t}` : ""}`;
  } else if (item.action === "auth.login") {
    text = "Admin signed in";
  } else if (item.action === "auth.logout") {
    text = "Admin signed out";
  } else if (t) {
    const label = SECTION_LABELS[t] || t.replace(/_/g, " ");
    text = `${label} ${meta.verb}`;
  } else {
    text = `Content ${meta.verb}`;
  }
  return { text, tone: meta.tone, icon: meta.icon };
};
const ActivityRow = ({ item }) => {
  const f = friendlyActivity(item);
  const Icon = f.icon;
  return (
    <li className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0">
      <div className={`h-8 w-8 shrink-0 rounded-lg flex items-center justify-center ${f.tone}`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-900 truncate">{f.text}</div>
        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
          <span className="truncate">{item.actor || "system"}</span>
          <span className="text-slate-300">•</span>
          <span title={fmtDate(item.ts)} className="whitespace-nowrap">{timeAgo(item.ts)}</span>
        </div>
      </div>
    </li>
  );
};

/* ---------- main page ---------- */
export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const lastLogin = useMemo(() => localStorage.getItem("wappit_last_login"), []);

  // Onboarding state
  const [onboardingStats, setOnboardingStats] = useState(null);
  const [onboardingRecent, setOnboardingRecent] = useState([]);
  const [chartRange, setChartRange] = useState("30d");
  const [chartData, setChartData] = useState([]);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [notifOpen, setNotifOpen] = useState(false);
  const [popup, setPopup] = useState(null); // { count, applications }
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const d = await fetchDashboard();
      setData(d);
    } catch {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const loadOnboarding = async () => {
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/onboarding/stats").then((x) => x.data),
        api.get("/admin/onboarding/recent", { params: { limit: 5 } }).then((x) => x.data),
      ]);
      setOnboardingStats(s);
      setOnboardingRecent(r);
    } catch { /* ignore */ }
  };

  const loadChart = async (range) => {
    try {
      const r = await api.get("/admin/onboarding/chart", { params: { range } }).then((x) => x.data);
      setChartData(r.buckets || []);
    } catch { /* ignore */ }
  };

  const loadNotifs = async () => {
    try {
      const r = await api.get("/admin/onboarding-notifications", { params: { limit: 25 } }).then((x) => x.data);
      setNotifs(r);
    } catch { /* ignore */ }
  };

  const loadPopup = async () => {
    try {
      const r = await api.get("/admin/onboarding-notifications/popup").then((x) => x.data);
      if (r.count > 0) setPopup(r);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    loadOnboarding();
    loadChart(chartRange);
    loadNotifs();
    loadPopup();
    const id = setInterval(() => setNow(new Date()), 60_000);
    localStorage.setItem("wappit_last_login", new Date().toISOString());
    return () => clearInterval(id);
  }, []);

  useEffect(() => { loadChart(chartRange); }, [chartRange]);

  const onLogout = () => {
    logout();
    toast.success("Signed out");
    nav("/admin/login");
  };

  const dismissPopup = async () => {
    setPopup(null);
    try { await api.post("/admin/onboarding-notifications/popup/dismiss"); } catch { /* ignore */ }
  };

  const markNotifsRead = async () => {
    try {
      await api.post("/admin/onboarding-notifications/mark-read");
      loadNotifs();
    } catch { /* ignore */ }
  };

  const submitSearch = (e) => {
    e?.preventDefault?.();
    if (search.trim()) nav(`/admin/onboarding?q=${encodeURIComponent(search.trim())}`);
  };

  const ov = data?.overview || {};
  const status = data?.status || {};
  const seo = data?.seo || {};
  const recent = (data?.recent_activity || []).slice(0, 10);

  return (
    <div className="min-h-screen bg-[#F8FAFC] relative" data-testid="admin-dashboard">
      {/* ambient gradient */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-gradient-to-b from-blue-50/60 via-white to-transparent" />

      {/* Header */}
      <header className="relative bg-white/70 backdrop-blur-xl border-b border-slate-200/70 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="hidden sm:inline-flex items-center text-[11px] font-bold tracking-wider uppercase px-2 py-1 rounded-full bg-slate-900 text-white">
              Admin
            </span>
          </div>

          {/* Quick search */}
          <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-md mx-4" data-testid="dashboard-search-form">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search company, ref ID, mobile…"
                className="pl-9 rounded-full bg-white/80 border-slate-200 h-9"
                data-testid="dashboard-search-input" />
            </div>
          </form>

          <div className="flex items-center gap-2 sm:gap-3">
            <a href="/" target="_blank" rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
              data-testid="admin-view-site">
              <ExternalLink className="h-3.5 w-3.5" /> View site
            </a>

            {/* Notification bell */}
            <div className="relative">
              <button
                type="button"
                onClick={() => { setNotifOpen((v) => !v); if (!notifOpen) markNotifsRead(); }}
                className="relative h-9 w-9 rounded-full bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center"
                data-testid="notification-bell"
              >
                <Bell className="h-4 w-4 text-slate-700" />
                {notifs.unread > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-badge">
                    {notifs.unread}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 mt-2 w-[min(340px,calc(100vw-2rem))] rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden z-50" data-testid="notification-panel">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                    <div className="text-sm font-bold text-slate-900">Notifications</div>
                    <button onClick={() => setNotifOpen(false)} className="text-xs text-slate-500 hover:text-slate-900">Close</button>
                  </div>
                  <ul className="max-h-[320px] overflow-y-auto divide-y divide-slate-100">
                    {notifs.items.length === 0 && (
                      <li className="px-4 py-6 text-center text-sm text-slate-500">No notifications yet</li>
                    )}
                    {notifs.items.map((n) => (
                      <li key={n.id} className="px-4 py-3 hover:bg-slate-50 cursor-pointer flex items-start gap-3" onClick={() => { setNotifOpen(false); nav(`/admin/onboarding?id=${n.app_id}`); }} data-testid={`notif-${n.id}`}>
                        <div className="h-8 w-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center"><Rocket className="h-4 w-4" /></div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-slate-900 truncate">{n.message}</div>
                          <div className="text-[11px] text-slate-500">{n.ref_id} · {timeAgo(n.ts)}</div>
                        </div>
                        {!n.read && <span className="mt-1 h-2 w-2 rounded-full bg-rose-500" />}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <Button variant="outline" size="sm" onClick={() => nav("/admin/leads")} className="rounded-full hidden md:inline-flex" data-testid="admin-open-leads">
              <Users className="h-3.5 w-3.5 mr-1.5" /> Leads
            </Button>
            <Button variant="outline" size="sm" onClick={() => nav("/admin/blog")} className="rounded-full" data-testid="admin-open-blog">
              Blog
            </Button>
            <Button variant="outline" size="sm" onClick={() => nav("/admin/cms")} className="rounded-full" data-testid="admin-open-cms">
              Open CMS
            </Button>
            <button
              type="button"
              onClick={() => nav("/admin/profile")}
              data-testid="admin-open-profile"
              className="hidden sm:flex items-center gap-2 pl-2 ml-1 border-l border-slate-200 hover:bg-slate-50 rounded-full pr-2 py-1 -my-1 transition-colors"
              title="View profile"
            >
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-600 to-emerald-500 text-white text-xs font-bold flex items-center justify-center overflow-hidden">
                {user?.avatar_url ? (
                  <img src={user.avatar_url.startsWith("http") ? user.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`} alt="" className="h-full w-full object-cover" />
                ) : (
                  initials(user?.email || "AD")
                )}
              </div>
              <div className="leading-tight hidden md:block text-left">
                <div className="text-xs text-slate-500">Signed in</div>
                <div className="text-xs font-semibold text-slate-900" data-testid="admin-user-email">{user?.email}</div>
              </div>
            </button>
            <Button variant="outline" size="sm" onClick={onLogout} className="rounded-full" data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="relative max-w-7xl mx-auto px-6 lg:px-8 py-8 sm:py-10">
        {/* Welcome */}
        <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 text-white p-6 sm:p-8 ring-1 ring-slate-900/40 shadow-[0_20px_50px_-20px_rgba(2,6,23,0.5)]">
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-blue-300/80">
                <Sparkles className="h-3.5 w-3.5" /> Wappit Admin Console
              </div>
              <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                {greeting()}, <span className="bg-gradient-to-r from-blue-300 via-emerald-300 to-teal-200 bg-clip-text text-transparent">{user?.name || "Admin"}</span>
              </h1>
              <p className="mt-2 text-slate-300 text-sm sm:text-base max-w-xl">
                Here&apos;s a snapshot of your website. Manage content, monitor system health and review recent activity — all in one place.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:gap-4 w-full lg:min-w-[420px]">
              <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 px-4 py-3 backdrop-blur">
                <div className="text-[10px] uppercase tracking-wider font-bold text-blue-200/70">Right now</div>
                <div className="mt-1 text-sm font-semibold tabular-nums">{now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</div>
                <div className="text-[11px] text-slate-300">{now.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}</div>
              </div>
              <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 px-4 py-3 backdrop-blur">
                <div className="text-[10px] uppercase tracking-wider font-bold text-blue-200/70">Last login</div>
                <div className="mt-1 text-sm font-semibold">{lastLogin ? timeAgo(lastLogin) : "First visit"}</div>
                <div className="text-[11px] text-slate-300 truncate" title={lastLogin || ""}>{lastLogin ? new Date(lastLogin).toLocaleDateString() : "Welcome aboard"}</div>
              </div>
              <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 px-4 py-3 backdrop-blur">
                <div className="text-[10px] uppercase tracking-wider font-bold text-blue-200/70">Last update</div>
                <div className="mt-1 text-sm font-semibold">{status.last_updated ? timeAgo(status.last_updated) : "—"}</div>
                <div className="text-[11px] text-slate-300">website content</div>
              </div>
            </div>
          </div>
        </section>

        {/* Overview cards */}
        <section className="mt-8" data-testid="overview-grid">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-slate-500 flex items-center gap-2">
              <LayoutDashboard className="h-3.5 w-3.5" /> Content overview
            </h2>
            <button onClick={load} className="text-xs font-semibold text-slate-500 hover:text-slate-900 flex items-center gap-1.5" data-testid="refresh-dashboard">
              <RefreshCcw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4">
            <StatTile testId="stat-sections"    icon={Layers}        tone="blue"    label="Sections"    value={ov.total_sections}     hint="all" onClick={() => nav("/admin/cms")} />
            <StatTile testId="stat-published"   icon={CheckCircle2}  tone="emerald" label="Published"   value={ov.published_sections} hint="live" onClick={() => nav("/admin/cms")} />
            <StatTile testId="stat-hidden"      icon={EyeOff}        tone="slate"   label="Hidden"      value={ov.hidden_sections}    hint="draft" onClick={() => nav("/admin/cms")} />
            <StatTile testId="stat-features"    icon={Sparkles}      tone="violet"  label="Features"    value={ov.total_features}     onClick={() => nav("/admin/cms?key=features")} />
            <StatTile testId="stat-pricing"     icon={IndianRupee}   tone="emerald" label="Pricing"     value={ov.total_pricing_plans} hint="plans" onClick={() => nav("/admin/cms?key=pricing")} />
            <StatTile testId="stat-testimonials" icon={Star}         tone="amber"   label="Testimonials" value={ov.total_testimonials} onClick={() => nav("/admin/cms?key=testimonials")} />
            <StatTile testId="stat-faqs"        icon={HelpCircle}    tone="blue"    label="FAQs"        value={ov.total_faqs}         onClick={() => nav("/admin/cms?key=faq")} />
            <StatTile testId="stat-industries"  icon={Building2}     tone="teal"    label="Industries"  value={ov.total_industries}   onClick={() => nav("/admin/cms?key=industries")} />
            <StatTile testId="stat-developer-api" icon={Plug}         tone="rose"    label="API Examples" value={ov.total_api_examples ?? 0} onClick={() => nav("/admin/cms?key=developer_api")} />
            <StatTile testId="stat-logos"       icon={MessageSquare} tone="slate"   label="Client logos" value={ov.total_client_logos} onClick={() => nav("/admin/cms?key=trust_stats")} />
            <StatTile testId="stat-media"       icon={ImageIcon}     tone="violet"  label="Media files" value={ov.total_media_files}  onClick={() => nav("/admin/cms")} />
          </div>
        </section>

        {/* WhatsApp Onboarding section */}
        <section className="mt-8" data-testid="onboarding-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-slate-500 flex items-center gap-2">
              <Rocket className="h-3.5 w-3.5" /> WhatsApp Onboarding
            </h2>
            <button onClick={() => nav("/admin/onboarding")} className="text-xs font-semibold text-emerald-700 hover:underline inline-flex items-center gap-1" data-testid="onboarding-view-all">
              View all <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="onboarding-overview">
            <OnboardingTile label="Total"               value={onboardingStats?.total ?? 0}                                tone="slate"   onClick={() => nav("/admin/onboarding")} />
            <OnboardingTile label="Pending"             value={onboardingStats?.by_status?.pending ?? 0}                  tone="amber"   onClick={() => nav("/admin/onboarding?status=pending")} />
            <OnboardingTile label="Documents required"  value={onboardingStats?.by_status?.documents_required ?? 0}       tone="orange"  onClick={() => nav("/admin/onboarding?status=documents_required")} />
            <OnboardingTile label="Under review"        value={onboardingStats?.by_status?.under_review ?? 0}             tone="blue"    onClick={() => nav("/admin/onboarding?status=under_review")} />
            <OnboardingTile label="Approved"            value={onboardingStats?.by_status?.approved ?? 0}                 tone="emerald" onClick={() => nav("/admin/onboarding?status=approved")} />
            <OnboardingTile label="Rejected"            value={onboardingStats?.by_status?.rejected ?? 0}                 tone="rose"    onClick={() => nav("/admin/onboarding?status=rejected")} />
            <OnboardingTile label="Completed"           value={onboardingStats?.by_status?.completed ?? 0}                tone="violet"  onClick={() => nav("/admin/onboarding?status=completed")} />
          </div>

          {/* Chart + recent widget */}
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <Activity className="h-4 w-4 text-emerald-600" /> Applications over time
                </h3>
                <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1" data-testid="chart-range">
                  {[{ k: "7d", l: "7d" }, { k: "30d", l: "30d" }, { k: "12m", l: "12m" }].map((r) => (
                    <button key={r.k} onClick={() => setChartRange(r.k)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${chartRange === r.k ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                      data-testid={`chart-range-${r.k}`}>
                      {r.l}
                    </button>
                  ))}
                </div>
              </div>
              <BarChart buckets={chartData} />
              <Legend />
            </div>

            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <Inbox className="h-4 w-4 text-blue-600" /> Recent onboarding
                </h3>
                <button onClick={() => nav("/admin/onboarding")} className="text-xs font-semibold text-blue-700 hover:underline">View all</button>
              </div>
              {onboardingRecent.length === 0 ? (
                <div className="py-10 text-center" data-testid="onboarding-recent-empty">
                  <Inbox className="h-7 w-7 mx-auto text-slate-300" />
                  <div className="mt-2 text-sm font-semibold text-slate-700">No applications yet</div>
                  <div className="text-xs text-slate-500">Submissions will appear here.</div>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100" data-testid="onboarding-recent-list">
                  {onboardingRecent.map((r) => (
                    <li key={r.id} onClick={() => nav(`/admin/onboarding?id=${r.id}`)} className="py-2.5 cursor-pointer hover:bg-slate-50 -mx-2 px-2 rounded-lg">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{r.company_name}</div>
                          <div className="text-[11px] text-slate-500 font-mono">{r.ref_id} · {timeAgo(r.created_at)}</div>
                        </div>
                        <StatusChip status={r.status} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>

        {/* Two-column zone: System Status + SEO */}
        <section className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* System status */}
          <div className="rounded-2xl bg-white border border-slate-200/80 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-600" /> System status
              </h3>
              <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">All operational</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5" data-testid="system-status">
              <StatusPill ok={status.website === "online"}     label="Website"   sub={status.website || "checking"}   icon={Globe} />
              <StatusPill ok={status.api === "healthy"}        label="API"       sub={status.api || "checking"}        icon={Activity} />
              <StatusPill ok={status.database === "connected"} label="Database"  sub={status.database || "checking"}   icon={Database} />
            </div>
          </div>

          {/* SEO Overview */}
          <div className="rounded-2xl bg-white border border-slate-200/80 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Globe className="h-4 w-4 text-blue-600" /> SEO overview
              </h3>
              <button onClick={() => nav("/admin/cms?key=seo")} className="text-xs font-semibold text-blue-700 hover:underline inline-flex items-center gap-1">
                Edit <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="rounded-xl bg-slate-50 px-3 py-2 mb-2" data-testid="seo-overview">
              <SeoRow label="Sitemap.xml"     ok={!!seo.sitemap} />
              <SeoRow label="Meta title"      ok={!!seo.meta_title} />
              <SeoRow label="Meta description" ok={!!seo.meta_description} />
              <SeoRow label="Favicon"         ok={!!seo.favicon} />
              <SeoRow label="Open Graph"      ok={!!seo.open_graph} />
            </div>
            <a href="/sitemap.xml" target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
              View sitemap <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </section>

        {/* Recent activity + Quick actions */}
        <section className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200/80 p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <Activity className="h-4 w-4 text-slate-700" /> Recent activity
              </h3>
              <span className="text-xs text-slate-500">Latest {recent.length}</span>
            </div>
            {loading ? (
              <div className="py-10 text-center text-sm text-slate-500">Loading activity…</div>
            ) : recent.length === 0 ? (
              <div className="py-10 text-center" data-testid="activity-empty">
                <div className="mx-auto h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-2">
                  <Clock className="h-5 w-5 text-slate-400" />
                </div>
                <div className="text-sm font-semibold text-slate-700">No recent activity yet</div>
                <div className="text-xs text-slate-500 mt-1">Make a change in the CMS and it&apos;ll appear here.</div>
              </div>
            ) : (
              <ul className="divide-slate-100" data-testid="activity-list">
                {recent.map((a, i) => <ActivityRow key={`${a.ts}-${i}`} item={a} />)}
              </ul>
            )}
          </div>

          <div className="rounded-2xl bg-white border border-slate-200/80 p-5">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-3">
              <Megaphone className="h-4 w-4 text-rose-500" /> Quick actions
            </h3>
            <div className="grid grid-cols-2 gap-2.5">
              <QuickAction icon={Rocket}        label="Onboarding"       onClick={() => nav("/admin/onboarding")}              testId="qa-onboarding" />
              <QuickAction icon={Users}         label="Leads"            onClick={() => nav("/admin/leads")}                   testId="qa-leads" />
              <QuickAction icon={Mail}          label="Newsletter"       onClick={() => nav("/admin/newsletter")}              testId="qa-newsletter" />
              <QuickAction icon={Sparkles}      label="Edit Hero"        onClick={() => nav("/admin/cms?key=hero")}            testId="qa-hero" />
              <QuickAction icon={Layers}        label="Edit Navigation"  onClick={() => nav("/admin/cms?key=navbar")}          testId="qa-navbar" />
              <QuickAction icon={Sparkles}      label="Edit Features"    onClick={() => nav("/admin/cms?key=features")}        testId="qa-features" />
              <QuickAction icon={IndianRupee}   label="Edit Pricing"     onClick={() => nav("/admin/cms?key=pricing")}         testId="qa-pricing" />
              <QuickAction icon={Star}          label="Testimonials"     onClick={() => nav("/admin/cms?key=testimonials")}    testId="qa-testimonials" />
              <QuickAction icon={HelpCircle}    label="FAQs"             onClick={() => nav("/admin/cms?key=faq")}             testId="qa-faqs" />
              <QuickAction icon={Layers}        label="Edit Footer"      onClick={() => nav("/admin/cms?key=footer")}          testId="qa-footer" />
              <QuickAction icon={Folder}        label="Media Library"    onClick={() => toast.message("Media Library coming next")} testId="qa-media" />
              <QuickAction icon={Settings2}     label="Global Settings"  onClick={() => nav("/admin/cms?key=global_settings")} testId="qa-settings" />
            </div>

            <div className="mt-5 rounded-xl bg-gradient-to-br from-blue-50 to-emerald-50 border border-blue-100 p-4">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-white shadow-sm flex items-center justify-center text-blue-700">
                  <MessageCircle className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">Need a hand?</div>
                  <div className="text-xs text-slate-600">Manage everything on your site without touching code.</div>
                  <button onClick={() => nav("/admin/cms")} className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-blue-700">
                    Open CMS <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer className="mt-10 mb-2 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
          <span>Wappit Admin · v1.0</span>
          <span>Session expires in 24 hours · Auto-refresh enabled</span>
        </footer>
      </main>

      {popup && <NewApplicationPopup popup={popup} onDismiss={dismissPopup} onView={(id) => { dismissPopup(); nav(id ? `/admin/onboarding?id=${id}` : "/admin/onboarding"); }} />}
    </div>
  );
}

const QuickAction = ({ icon: Icon, label, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group flex items-center gap-2 rounded-xl border border-slate-200 hover:border-slate-300 bg-white px-3 py-2.5 text-left hover:bg-slate-50 transition-colors"
  >
    <span className="h-7 w-7 rounded-lg bg-slate-900 text-white flex items-center justify-center group-hover:scale-110 transition-transform">
      <Icon className="h-3.5 w-3.5" />
    </span>
    <span className="text-xs font-semibold text-slate-800 truncate">{label}</span>
    <ChevronRight className="ml-auto h-3.5 w-3.5 text-slate-400 group-hover:text-slate-700" />
  </button>
);

/* ---------- Onboarding helpers ---------- */
const ONBO_TONES = {
  slate:   "from-slate-50 to-white text-slate-700 ring-slate-200",
  amber:   "from-amber-50 to-white text-amber-700 ring-amber-100",
  orange:  "from-orange-50 to-white text-orange-700 ring-orange-100",
  blue:    "from-blue-50 to-white text-blue-700 ring-blue-100",
  emerald: "from-emerald-50 to-white text-emerald-700 ring-emerald-100",
  rose:    "from-rose-50 to-white text-rose-700 ring-rose-100",
  violet:  "from-violet-50 to-white text-violet-700 ring-violet-100",
};
const OnboardingTile = ({ label, value, tone = "slate", onClick }) => (
  <button onClick={onClick}
    className="group relative text-left rounded-2xl bg-white border border-slate-200/80 p-4 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgb(2,6,23,0.06)] transition-all overflow-hidden"
    data-testid={`onboarding-stat-${label.toLowerCase().replace(/\s+/g,"-")}`}>
    <div className={`pointer-events-none absolute -top-10 -right-10 h-24 w-24 rounded-full bg-gradient-to-br ${ONBO_TONES[tone]} opacity-80`} />
    <div className="relative text-[11px] uppercase tracking-[0.12em] font-bold text-slate-500">{label}</div>
    <div className="relative mt-2 text-2xl font-extrabold text-slate-900 tabular-nums" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
  </button>
);

const StatusChip = ({ status }) => {
  const map = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    documents_required: "bg-orange-50 text-orange-700 border-orange-200",
    under_review: "bg-blue-50 text-blue-700 border-blue-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-rose-50 text-rose-700 border-rose-200",
    completed: "bg-slate-900 text-white border-slate-900",
  };
  const label = (status || "pending").replace(/_/g, " ").replace(/\b\w/g, (c)=>c.toUpperCase());
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${map[status] || map.pending}`}>{label}</span>;
};

function BarChart({ buckets }) {
  if (!buckets || buckets.length === 0) {
    return <div className="h-32 flex items-center justify-center text-sm text-slate-500" data-testid="chart-empty">No applications in this range yet.</div>;
  }
  const max = Math.max(1, ...buckets.map((b) => b.total));
  return (
    <div className="mt-1 grid items-end gap-1.5 h-40" style={{ gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))` }} data-testid="chart-bars">
      {buckets.map((b) => {
        const totalH = Math.max(2, Math.round((b.total / max) * 130));
        const approvedH = b.total ? Math.round((b.approved / max) * 130) : 0;
        const rejectedH = b.total ? Math.round((b.rejected / max) * 130) : 0;
        const completedH = b.total ? Math.round((b.completed / max) * 130) : 0;
        return (
          <div key={b.date} className="flex flex-col items-center justify-end gap-1 group" title={`${b.date}: ${b.total}`}>
            <div className="text-[10px] font-bold text-slate-700 tabular-nums opacity-0 group-hover:opacity-100 transition-opacity">{b.total}</div>
            <div className="w-full rounded-md bg-slate-100 relative overflow-hidden" style={{ height: totalH }}>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-300 to-slate-200" style={{ height: totalH }} />
              {completedH > 0 && <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-violet-500 to-violet-400" style={{ height: completedH }} />}
              {approvedH > 0 && <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-emerald-500 to-emerald-400" style={{ height: approvedH }} />}
              {rejectedH > 0 && <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-rose-500 to-rose-400 mix-blend-multiply" style={{ height: rejectedH, opacity: .8 }} />}
            </div>
            <div className="text-[9px] text-slate-500 truncate w-full text-center">{b.date.slice(-5)}</div>
          </div>
        );
      })}
    </div>
  );
}

const Legend = () => (
  <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-slate-600">
    <span className="inline-flex items-center gap-1.5"><span className="h-2 w-3 rounded bg-slate-300" /> Total</span>
    <span className="inline-flex items-center gap-1.5"><span className="h-2 w-3 rounded bg-emerald-500" /> Approved</span>
    <span className="inline-flex items-center gap-1.5"><span className="h-2 w-3 rounded bg-rose-500" /> Rejected</span>
    <span className="inline-flex items-center gap-1.5"><span className="h-2 w-3 rounded bg-violet-500" /> Completed</span>
  </div>
);

function NewApplicationPopup({ popup, onDismiss, onView }) {
  const multiple = popup.count > 1;
  const first = popup.applications?.[0];

  // Lock body scroll while popup is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const content = (
    <AnimatePresence>
      <motion.div
        key="new-app-popup"
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6"
        data-testid="new-application-popup"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <motion.div
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
          onClick={onDismiss}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        />
        <motion.div
          className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden"
          initial={{ opacity: 0, scale: 0.92, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 16 }}
          transition={{ type: "spring", stiffness: 280, damping: 24 }}
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-gradient-to-br from-slate-900 via-blue-950 to-emerald-900 text-white p-5 relative">
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Close"
              data-testid="popup-x"
              className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-300">
              <Rocket className="h-3.5 w-3.5" /> Onboarding alert
            </div>
            <h3 className="mt-2 text-2xl font-bold tracking-tight pr-8" style={{ fontFamily: "Outfit, sans-serif" }}>
              {multiple ? `📢 ${popup.count} new onboarding requests` : "🎉 New onboarding request"}
            </h3>
            {!multiple && first && (
              <div className="mt-3 rounded-xl bg-white/10 ring-1 ring-white/15 p-3">
                <div className="text-[11px] uppercase tracking-wider text-blue-200 font-bold">Company</div>
                <div className="text-base font-bold">{first.company_name}</div>
                <div className="mt-1 text-[11px] uppercase tracking-wider text-blue-200 font-bold">Reference ID</div>
                <div className="font-mono text-sm">{first.ref_id}</div>
              </div>
            )}
            {multiple && (
              <p className="mt-3 text-sm text-slate-200">Multiple new applications arrived since your last visit. Review them in the onboarding queue.</p>
            )}
          </div>
          <div className="p-4 flex items-center justify-end gap-2">
            <Button variant="outline" onClick={onDismiss} className="rounded-full" data-testid="popup-close">
              Close
            </Button>
            <Button onClick={() => onView(multiple ? null : first?.id)} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="popup-view">
              {multiple ? "Review applications" : "View application"} <ArrowUpRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return typeof document !== "undefined" ? createPortal(content, document.body) : null;
}
